
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useUserProfile } from "@/hooks/useUserProfile";

const formSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  price: z.string().min(1, "Le prix est requis"),
  minimum_order_amount: z.string().optional(),
  free_delivery_threshold: z.string().optional(),
  estimated_time: z.string().optional(),
});

interface DeliveryZoneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zone?: any;
  onSuccess: () => void;
}

export const DeliveryZoneDialog = ({
  open,
  onOpenChange,
  zone,
  onSuccess,
}: DeliveryZoneDialogProps) => {
  const { data: profile } = useUserProfile();
  
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      price: "",
      minimum_order_amount: "",
      free_delivery_threshold: "",
      estimated_time: "",
    },
  });

  useEffect(() => {
    if (zone) {
      form.reset({
        name: zone.name,
        price: zone.price.toString(),
        minimum_order_amount: zone.minimum_order_amount?.toString() || "",
        free_delivery_threshold: zone.free_delivery_threshold?.toString() || "",
        estimated_time: zone.estimated_time || "",
      });
    } else {
      form.reset({
        name: "",
        price: "",
        minimum_order_amount: "",
        free_delivery_threshold: "",
        estimated_time: "",
      });
    }
  }, [zone, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!profile?.id) {
      toast.error("Utilisateur non connecté");
      return;
    }

    try {
      const data = {
        name: values.name,
        price: parseFloat(values.price),
        minimum_order_amount: values.minimum_order_amount
          ? parseFloat(values.minimum_order_amount)
          : null,
        free_delivery_threshold: values.free_delivery_threshold
          ? parseFloat(values.free_delivery_threshold)
          : null,
        estimated_time: values.estimated_time || null,
        owner_id: profile.id
      };

      if (zone) {
        const { error } = await supabase
          .from("delivery_zones")
          .update(data)
          .eq("id", zone.id);
        if (error) throw error;
        toast.success("Zone de livraison mise à jour");
      } else {
        const { error } = await supabase.from("delivery_zones").insert([data]);
        if (error) throw error;
        toast.success("Zone de livraison créée");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {zone ? "Modifier la zone" : "Nouvelle zone de livraison"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom de la zone</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ex: Centre ville" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prix de livraison (FCFA)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="Ex: 1000"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="minimum_order_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Montant minimum de commande (FCFA)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="Ex: 5000"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="free_delivery_threshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Livraison gratuite à partir de (FCFA)
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="Ex: 20000"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="estimated_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Temps de livraison estimé</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Ex: 30-45 minutes"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Annuler
              </Button>
              <Button type="submit">
                {zone ? "Modifier" : "Créer"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
